import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
  IDepartmentNode,
  ProductFilterValues,
  countProductFilters,
  flattenDepartmentTree,
  formatNamesWithCount,
  nodeKey,
  selectedDepartmentKeys,
} from "./productFilterUtils";

import "./productsAppliedFilters.scss";

export type AppliedFilterGroup = "type" | "availability" | "vendor" | "departments";

interface IProps {
  filters: ProductFilterValues;
  vendors: Array<{ id: number; title: string }>;
  departmentTree: IDepartmentNode[];
  onRemoveGroup: (group: AppliedFilterGroup) => void;
  onClearAll: () => void;
  onEditFilters: () => void;
}

export default function ProductsAppliedFilters(props: IProps) {
  const { filters, vendors, departmentTree, onRemoveGroup, onClearAll, onEditFilters } = props;
  const { t } = useTranslation();

  if (countProductFilters(filters) === 0) {
    return null;
  }

  const chips: Array<{ group: AppliedFilterGroup; label: string; value: string }> = [];

  if (filters.type.length > 0) {
    chips.push({ group: "type", label: t("Type"), value: formatNamesWithCount(filters.type, 2) });
  }

  if (filters.sales_channel_type || filters.all_facilities) {
    const availability = [
      filters.sales_channel_type === "online_store" ? t("Online store") : t("All"),
      filters.all_facilities ? t("Other facilities") : null,
    ].filter(Boolean);
    chips.push({ group: "availability", label: t("Availability"), value: availability.join(", ") });
  }

  if (filters.vendor_id.length > 0) {
    const vendorNames = filters.vendor_id.map(id => vendors?.find(vendor => vendor.id === id)?.title ?? String(id));
    chips.push({ group: "vendor", label: t("Vendor"), value: formatNamesWithCount(vendorNames, 2) });
  }

  const selectedKeys = selectedDepartmentKeys(filters);
  if (selectedKeys.size > 0) {
    const departmentNames = flattenDepartmentTree(departmentTree)
      .filter(node => selectedKeys.has(nodeKey(node)))
      .map(node => node.title);
    chips.push({ group: "departments", label: t("Departments"), value: formatNamesWithCount(departmentNames, 2) });
  }

  return (
    <div className="products-applied-filters">
      {chips.map(chip => (
        <div key={chip.group} className="products-applied-filters-chip">
          <span>
            <strong>{chip.label}:</strong> {chip.value}
          </span>
          <button
            type="button"
            className="products-applied-filters-chip-remove"
            aria-label={t("Remove {{label}} filter", { label: chip.label })}
            onClick={() => onRemoveGroup(chip.group)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      ))}
      <div className="products-applied-filters-actions">
        <button type="button" className="products-applied-filters-action" onClick={onClearAll}>
          <FontAwesomeIcon icon={faXmark} />
          {t("Clear all")}
        </button>
        <span className="products-applied-filters-divider" aria-hidden="true" />
        <button type="button" className="products-applied-filters-action" onClick={onEditFilters}>
          {t("Edit filters")}
        </button>
      </div>
    </div>
  );
}
